import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MetricBarType } from './m-confidence-metric-bar.component.types';
import type { ConfidenceRatingMetricDTO } from '@libs/api';
import { NgClass } from '@angular/common';
import { MTooltipComponent } from '../m-tooltip/m-tooltip.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'm-confidence-metric-bar',
  templateUrl: './m-confidence-metric-bar.component.html',
  styleUrls: ['./m-confidence-metric-bar.component.scss'],
  imports: [NgClass, MTooltipComponent]
})
export class MConfidenceMetricBarComponent {
  @Input() label = '';
  @Input() metric!: ConfidenceRatingMetricDTO;
  @Input() type: MetricBarType = MetricBarType.TwoZone;
  @Input() minRange = 0;
  @Input() maxRange?: number;
  @Input() threshold?: number;
  @Input() invertColors = false;
  @Input() tooltipTitle = '';
  @Input() tooltipContent = '';
  @Input() tooltipDescription = '';
  @Input() showMinRangeLabel = true;
  @Input() showMaxRangeLabel = false;

  /**
   * Returns the numeric value from the metric
   */
  get value(): number {
    return Number(this.metric.value);
  }

  /**
   * Returns effective maxRange (uses threshold * 2 as fallback if not provided)
   */
  get effectiveMaxRange(): number {
    if (this.maxRange !== undefined) {
      return this.maxRange;
    }
    if (this.threshold) {
      return this.threshold * 2;
    }
    return 1;
  }

  /**
   * Returns the position of the tick marker (0-100%)
   * For TwoZone: threshold is always at 50%, values scale relative to threshold
   */
  get tickPosition(): number {
    if (this.type === MetricBarType.TwoZone && this.threshold !== undefined) {
      if (this.value <= this.threshold) {
        // Left zone (0-50%): map minRange→threshold to 0-50%
        const range = this.threshold - this.minRange;
        return range === 0
          ? 50
          : Math.max(0, ((this.value - this.minRange) / range) * 50);
      }
      // Right zone (50-100%): map threshold→maxRange to 50-100%
      // If value exceeds effectiveMaxRange, use value as maxRange for calculation
      const maxRange = Math.max(this.effectiveMaxRange, this.value);
      const range = maxRange - this.threshold;
      const position =
        range === 0 ? 50 : 50 + ((this.value - this.threshold) / range) * 50;
      // Clamp to 100% maximum
      return Math.min(100, position);
    }

    if (this.type === MetricBarType.ThreeZone && this.maxRange) {
      // Zone 1 (0-33.33%): below minRange
      if (this.value < this.minRange) {
        const positionInZone = (this.value / this.minRange) * 33.33;
        return Math.max(0, positionInZone);
      }

      // Zone 2 (33.33-66.66%): between minRange and maxRange (good zone)
      if (this.value >= this.minRange && this.value <= this.maxRange) {
        const zoneRange = this.maxRange - this.minRange;
        const valueInZone = this.value - this.minRange;
        const positionInZone = (valueInZone / zoneRange) * 33.33;
        return 33.33 + positionInZone;
      }

      // Zone 3 (66.66-100%): above maxRange
      if (this.value > this.maxRange) {
        const positionInZone = (this.value / this.maxRange) * 66.66;
        return Math.min(100, positionInZone);
      }
    }

    return 0;
  }

  /**
   * Returns whether the value is in the "good" zone
   */
  get isInGoodZone(): boolean {
    if (this.type === MetricBarType.TwoZone) {
      if (this.threshold === undefined) return false;
      return this.invertColors
        ? this.value >= this.threshold
        : this.value <= this.threshold;
    }

    if (this.type === MetricBarType.ThreeZone) {
      if (this.minRange === undefined || this.maxRange === undefined)
        return false;
      return this.value >= this.minRange && this.value <= this.maxRange;
    }

    return false;
  }

  /**
   * Returns text color class based on zone
   */
  get valueTextColor(): string {
    return this.isInGoodZone ? 'text-green-600' : 'text-red-600';
  }

  /**
   * Returns the start label for the progress bar
   */
  get startLabel(): string {
    if (this.type === MetricBarType.ThreeZone) {
      return this.minRange ? this.minRange.toString() : '';
    }
    return this.minRange.toFixed(2);
  }

  /**
   * Returns the middle label
   */
  get middleLabel(): string {
    if (this.type === MetricBarType.TwoZone && this.threshold) {
      return this.threshold.toFixed(2);
    }
    return this.maxRange ? this.maxRange.toString() : '';
  }

  /**
   * Returns the end label for the progress bar
   */
  get endLabel(): string {
    return this.maxRange ? this.maxRange.toFixed(2) : '';
  }

  /**
   * Returns position of start label in two-zone
   */
  get startLabelPosition(): number {
    if (this.type === MetricBarType.TwoZone) {
      return 0;
    }
    return 33.33;
  }

  /**
   * Returns position of middle label (threshold) in two-zone
   */
  get middleLabelPosition(): number {
    if (this.type === MetricBarType.TwoZone) {
      return 50;
    }
    return 66.66;
  }

  /**
   * Returns position of end label in two-zone
   */
  readonly endLabelPosition = 100;

  /**
   * Returns CSS classes for the progress bar zones
   */
  get progressBarClasses(): string {
    if (this.type === MetricBarType.TwoZone) {
      return this.invertColors ? 'two-zone-good-right' : 'two-zone-good-left';
    }
    if (this.type === MetricBarType.ThreeZone) {
      return 'three-zone';
    }
    return '';
  }
}
